/**
 * The `/programmes/:programmeId` layout route.
 *
 * Opens the programme into the legacy global store via the shared `editorSync`
 * controller from {@link WorkspaceOutletContext}, not a router `loader`:
 * `openProgramme` needs that live, stateful instance, which a loader running
 * outside component context cannot reach.
 *
 * @module router/programme-editor/ProgrammeLayout
 */

import React, { useCallback, useEffect } from "react";

import { Navigate, Outlet, useLocation, useMatches, useNavigate, useParams } from "react-router";

import { EditorBar } from "@/components/editor/EditorBar";
import { BackendEditorStatus } from "@/components/editor/EditorSyncStatus";
import { OverviewSummary } from "@/components/editor/OverviewSummary";
import { changeCaseKindLabel } from "@/components/shared/governance-labels";
import { programmeRoleFor } from "@/components/shared/programme-role";
import { WorkflowStateBadge } from "@/components/shared/WorkflowStateBadge";
import {
  countLabel,
  InfoAlert,
  LinkButton,
  PageHeader,
  Panel,
  WarningAlert,
} from "@/components/ui";
import { useProgramme } from "@/hooks/useStore";
import { EditorProgrammeSyncBoundary } from "@/platform";
import {
  EditorReadinessProvider,
  usePublishReadiness,
  useServerReadiness,
} from "@/platform/editor-readiness-context";
import { DepartureGuardScope, useDepartureGuardHost } from "@/router/departure-guard";
import { NavigationBlockerDialog } from "@/router/NavigationBlockerDialog";
import { editorAvailability } from "@/router/programme-editor/editor-availability";
import { ProgrammeSkeleton } from "@/router/programme-editor/ProgrammeSkeleton";
import { useEditorBase } from "@/router/programme-editor/SurfaceLayout";
import { pathForStateKey } from "@/router/programme-editor/surfaces";
import { useWorkspaceOutletContext } from "@/router/workspace-context";
import type { WorkflowState } from "@/server/types/workflow";

/** What a section route declares about itself, read off the deepest match. */
export interface SectionHandle {
  structural?: boolean;
  programmeShell?: boolean;
}

/** The builder's programme header: what the programme is and what they build in it. */
function BuilderHeader({
  title,
  workflowState,
  programmeId,
}: {
  title: string;
  workflowState: WorkflowState | undefined;
  programmeId: string | undefined;
}): React.ReactElement {
  const { session, platformModules } = useWorkspaceOutletContext();
  const owned = platformModules.filter(
    (module) => module.programmeId === programmeId && module.ownerUserId === session.user.id,
  ).length;
  return (
    <PageHeader
      eyebrow="Programme"
      title={title}
      description={
        <>
          {workflowState ? <WorkflowStateBadge state={workflowState} /> : null}{" "}
          <span data-testid="builder-programme-context">
            {`You build ${countLabel(owned, "module")} here. The programme is read only for you, and only your modules are shown.`}
          </span>
        </>
      }
    />
  );
}

/**
 * What a module builder reaches: their modules, the team, and a read-only
 * summary where the lead's Overview would be. Everything else is the lead's.
 */
function BuilderSurface({
  base,
  programmeId,
}: {
  base: string;
  programmeId: string | undefined;
}): React.ReactElement {
  const { pathname, search } = useLocation();
  const { programme } = useProgramme();
  const { session, platformModules } = useWorkspaceOutletContext();
  const surface = pathname.slice(base.length + 1).split("/")[0];
  // `modules/:moduleId` is the module's old address, forwarded into the studio.
  if (surface === "studio" || surface === "team" || surface === "modules") {
    return <Outlet />;
  }
  if (surface === "overview") {
    const owned = new Set(
      platformModules
        // Module ids are unique within a programme, not across them.
        .filter(
          (module) => module.programmeId === programmeId && module.ownerUserId === session.user.id,
        )
        .map((module) => module.moduleId),
    );
    return (
      <OverviewSummary programme={programme} ownedModuleIds={owned} studioPath={`${base}/studio`} />
    );
  }
  return <Navigate to={`${base}/studio${search}`} replace />;
}

export function ProgrammeLayout(): React.ReactElement {
  const { programmeId, changeCaseId } = useParams<{
    programmeId: string;
    changeCaseId?: string;
  }>();
  const { pathname } = useLocation();
  const {
    client,
    editorSync,
    canEditProgramme,
    canEditWorkflowState,
    session,
    shouldRenderReadOnlyEditorView,
    viewingProgramme,
  } = useWorkspaceOutletContext();
  const builder = programmeRoleFor(session, programmeId) === "builder";
  const { programme } = useProgramme();
  const { record, status, openProgramme, enterStructuralRegion } = editorSync;
  const editSession = editorSync.session;
  // A change case's working copy has no readiness of its own on the server;
  // the case's governance panel reports where it stands.
  const readiness = useServerReadiness({
    client,
    programmeId: record?.id,
    saveRevision: record?.saveRevision,
    enabled: !changeCaseId && !builder,
  });
  usePublishReadiness(readiness.result);
  const matches = useMatches();
  const navigate = useNavigate();
  const editorBase = useEditorBase();

  // A finding names a legacy step key; the registry is what turns it into the
  // surface address that key now lives at.
  const goToStateKey = useCallback(
    (stateKey: string, moduleId?: string) => {
      const to = pathForStateKey(editorBase, stateKey, moduleId);
      if (to) {
        navigate(to);
      }
    },
    [editorBase, navigate],
  );

  // The route says whether it edits structure, so the layout no longer parses
  // the pathname to work it out. The deepest match that declares it wins.
  // A builder never holds the programme lease, even for the moment before a
  // structural address redirects them to Module Studio. Nor does a viewer.
  const structural =
    !builder &&
    !viewingProgramme &&
    (matches
      .map((match) => (match.handle as SectionHandle | undefined)?.structural)
      .filter((value): value is boolean => value !== undefined)
      .at(-1) ??
      false);

  useEffect(() => {
    enterStructuralRegion(structural);
    // Leaving the editor is leaving the region; without this the lease outlives
    // the tab. Safe under StrictMode's double invoke: this releases a lease
    // rather than bumping the generation counter, and a second acquire is
    // refused while one is pending.
    return () => {
      enterStructuralRegion(false);
    };
  }, [enterStructuralRegion, structural]);

  useEffect(() => {
    if (!programmeId) {
      return;
    }
    // No cleanup calling closeProgramme(): openProgramme already releases
    // whatever lease is held at the start of every call, keyed off a ref
    // rather than this effect, so it self-cleans both when programmeId
    // changes and under StrictMode's synchronous mount/cleanup/mount.
    openProgramme(programmeId, changeCaseId);
  }, [changeCaseId, openProgramme, programmeId]);

  const { blocker, heldByOwn, settling, register } = useDepartureGuardHost({
    held: ({ nextLocation }) => {
      if (!editSession.pending) {
        return false;
      }
      // Moving between surfaces of the same programme stays inside this layout
      // and is never blocked; only leaving it entirely is, since that unmounts
      // the layout and discards the lease.
      const editorRoot = changeCaseId
        ? `/programmes/${programmeId}/changes/${changeCaseId}`
        : `/programmes/${programmeId}`;
      // A change case sits under the programme's address but is routed to a
      // layout of its own, so entering one leaves this one.
      const intoChangeCase =
        !changeCaseId && nextLocation.pathname.startsWith(`${editorRoot}/changes/`);
      return intoChangeCase || !nextLocation.pathname.startsWith(`${editorRoot}/`);
    },
    settle: editSession.leave,
  });

  if (status === "loading" && !record) {
    return (
      <ProgrammeSkeleton
        pathname={pathname}
        label="Opening the programme…"
        data-testid="programme-loading"
      />
    );
  }

  if (status === "error" && !record && changeCaseId) {
    // A discarded case's address lives on in notifications already sent.
    return (
      <Panel title="Programme editor" icon="pencil-simple">
        <WarningAlert data-testid="change-case-not-available">
          This change isn&apos;t available. It may have been discarded, or this account may not have
          access to it.
        </WarningAlert>
        <LinkButton to={`/programmes/${programmeId}`} icon="arrow-left" className="mt-4">
          Open the programme
        </LinkButton>
      </Panel>
    );
  }

  if (status === "error" && !record) {
    // Deliberately the same message whether the programme does not exist or
    // this account cannot reach it, so neither discloses cross-tenant existence.
    return (
      <Panel title="Programme editor" icon="pencil-simple">
        <WarningAlert data-testid="programme-not-available">
          This programme isn&apos;t available. It may not exist, or this account may not have access
          to it.
        </WarningAlert>
      </Panel>
    );
  }

  return (
    // The boundary wraps the whole editor, not just the sections: the bar's
    // save pill reads the sync through this context, and outside it the one
    // indicator the editor has could only ever report the local draft.
    <EditorProgrammeSyncBoundary value={editorSync}>
      <div className="space-y-4">
        <NavigationBlockerDialog
          blocker={blocker}
          settling={settling}
          itemLabel={heldByOwn ? "programme" : "module"}
          // Only leaving the programme leaves its module drafts behind.
          onLeave={heldByOwn ? editSession.discardModuleDrafts : undefined}
        />
        {builder ? (
          <BuilderHeader
            title={programme.title?.trim() || "Untitled programme"}
            workflowState={record?.workflowState}
            programmeId={programmeId}
          />
        ) : (
          <EditorBar
            onNavigateToStep={goToStateKey}
            readiness={readiness.result}
            readinessFailed={readiness.failed}
            onRetryReadiness={readiness.retry}
            readinessRetrying={readiness.retrying}
            readOnly={shouldRenderReadOnlyEditorView}
            availability={
              record
                ? editorAvailability({
                    readOnly: shouldRenderReadOnlyEditorView,
                    workflowState: record.workflowState,
                    canEditWorkflowState,
                    canEditProgramme,
                    viewing: viewingProgramme,
                  })
                : null
            }
          />
        )}
        <BackendEditorStatus
          sync={editorSync}
          moduleTitle={(moduleId) =>
            programme.modules?.find((module) => module.id === moduleId)?.title
          }
        />
        {record?.changeCaseId ? (
          <InfoAlert
            title={
              record.institutionalLabel ?? `${changeCaseKindLabel(record.changeCaseKind)} workspace`
            }
            data-testid="change-case-context"
          >
            Working revision of the approved curriculum this was opened from.
            {record.targetEffectiveFrom ? ` Effective from ${record.targetEffectiveFrom}.` : ""}
          </InfoAlert>
        ) : null}
        {/* On `record` alone. Adding `status !== "loading"` unmounted the whole
          editor on every re-read of an already-open programme, so a section's
          own state — a reviewer's half-typed note — went with it. The first
          load is already answered by the early return above. */}
        {/* The edit lock belongs to the sections, not to every surface:
          governance and team are what an account still does once the
          content itself is closed for editing. */}
        <EditorReadinessProvider value={readiness.result} failed={readiness.failed}>
          <DepartureGuardScope register={register}>
            {record && builder ? (
              <BuilderSurface base={editorBase} programmeId={programmeId} />
            ) : null}
            {record && !builder ? <Outlet /> : null}
          </DepartureGuardScope>
        </EditorReadinessProvider>
      </div>
    </EditorProgrammeSyncBoundary>
  );
}
